<template>
  <div class="kinderhort-statistik">
    <v-row justify="center" class="mb-6">
      <v-col cols="12" sm="8" md="6">
        <v-menu v-model="dateMenu" :close-on-content-click="false" transition="scale-transition" offset-y min-width="auto" dark>
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="formattedDateRange"
              label="Zeitraum auswählen"
              prepend-inner-icon="mdi-calendar-range"
              readonly
              dark
              solo-inverted
              rounded
              v-bind="attrs"
              v-on="on"></v-text-field>
          </template>
          <v-date-picker v-model="dateRange" range scrollable locale="de-CH">
            <v-spacer></v-spacer>
            <v-btn text color="grey" @click="dateMenu = false">Abbrechen</v-btn>
            <v-btn text color="primary" @click="applyDateRange">Anwenden</v-btn>
          </v-date-picker>
        </v-menu>
      </v-col>
    </v-row>

    <v-row v-if="totalChildren !== null && totalBabies !== null" class="justify-center mb-8">
      <v-col cols="auto">
        <v-chip dark large pill color="rgba(0,0,0,0.3)" class="pa-6">
          <v-icon left size="28" color="primary">mdi-account-group</v-icon>
          <span class="font-weight-bold text-h6"> {{ totalChildren }} Kinder &nbsp;|&nbsp; {{ totalBabies }} Babies </span>
        </v-chip>
      </v-col>
    </v-row>

    <div v-if="statistic && statistic.length > 0">
      <v-row>
        <v-col cols="12" md="10" lg="8" class="mx-auto" v-for="(entry, index) in statistic" :key="entry.date">
          <v-card dark class="day-card" :style="{ 'animation-delay': `${index * 100}ms` }">
            <v-card-title class="day-card-title">
              <span>
                <v-icon left>mdi-calendar-blank</v-icon>
                {{ entry.date === today ? "Heute" : entry.date === tomorrow ? "Morgen" : $moment(entry.date).format("dd,DD.MMMM YYYY") }}
              </span>
              <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon @click="addDaycareSignup(entry.date)" v-bind="attrs" v-on="on">
                    <v-icon color="primary" large>mdi-plus-circle</v-icon>
                  </v-btn>
                </template>
                <span>Neue Anmeldung für diesen Tag</span>
              </v-tooltip>
            </v-card-title>

            <v-divider class="mx-4"></v-divider>

            <v-list color="transparent" class="py-0">
              <div v-for="(signup, index) in entry.signups" :key="signup._id">
                <v-chip class="ml-5" v-if="signup.childPackageAmount" small color="white" outlined>
                  Anz. Kinderhort (TAC): {{ signup.childPackageAmount }}</v-chip
                >
                <v-chip class="ml-5" v-else-if="signup.user.daycareAmount && $moment().isBefore($moment('2025-09-23'))" small color="white" outlined>
                  Anz. Kinderhort (roody): {{ signup.user.daycareAmount }}</v-chip
                >

                <v-list-item class="signup-list-item">
                  <v-list-item-content class="my-0 py-0">
                    <v-list-item-title class="text-subtitle-1 font-weight-bold">
                      {{ signup.user.vorname }} {{ signup.user.nachname }}
                    </v-list-item-title>

                    <v-list-item-subtitle v-if="signup.arrivalTime"> Check-in: {{ signup.arrivalTime }} Uhr </v-list-item-subtitle>

                    <v-list-item-subtitle v-if="signup.childNames && signup.childNames.length" class="mt-1">
                      <v-icon x-small left color="primary">mdi-human-child</v-icon>
                      {{ signup.childNames.join(", ") }}
                    </v-list-item-subtitle>

                    <v-list-item-subtitle v-if="signup.location" class="mt-1">
                      <v-icon x-small left color="primary">{{ locationIcon(signup.location) }}</v-icon>
                      {{ locationLabel(signup.location) }}
                    </v-list-item-subtitle>

                    <v-list-item-subtitle v-if="signup.note" class="note-preview">
                      <v-icon x-small left color="primary">mdi-note-text-outline</v-icon>{{ signup.note }}
                    </v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action class="flex-row align-center">
                    <v-chip outlined small class="mx-1">
                      <v-icon small left>mdi-human-child</v-icon>
                      {{ signup.amountChildren || 0 }}
                    </v-chip>
                    <v-chip outlined small class="mx-1">
                      <v-icon small left>mdi-baby-carriage</v-icon>
                      {{ signup.amountBabies || 0 }}
                    </v-chip>
                    <v-btn icon small @click="editDaycareSignup(signup._id, entry)">
                      <v-icon small>mdi-pencil-outline</v-icon>
                    </v-btn>
                  </v-list-item-action>
                </v-list-item>
                <v-divider v-if="index < entry.signups.length - 1" class="mx-4"></v-divider>
              </div>
            </v-list>

            <v-divider v-if="entry.signups.length > 0" class="mx-4"></v-divider>

            <v-card-actions class="day-card-footer">
              <v-spacer></v-spacer>
              <span class="font-weight-bold">Total: {{ entry.childrenAmount }} Kinder & {{ entry.amountBabies }} Babies</span>
              <v-spacer></v-spacer>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <v-row v-else-if="statistic" class="justify-center">
      <v-col cols="12" class="text-center pa-8 mt-8">
        <v-icon x-large class="mb-4" color="grey lighten-1">mdi-emoticon-sad-outline</v-icon>
        <p class="text-h6 grey--text">Keine Anmeldungen im gewählten Zeitraum gefunden.</p>
      </v-col>
    </v-row>

    <v-dialog
      :fullscreen="$vuetify.breakpoint.smAndDown"
      v-model="showDaycareDialog"
      content-class="signup-dialog-wrapper"
      transition="dialog-bottom-transition"
      max-width="560"
      persistent>
      <v-card dark class="signup-dialog">
        <div class="dialog-header">
          <div class="dialog-header-bg"></div>
          <div class="dialog-header-content">
            <div class="d-flex align-center justify-space-between w-100">
              <div class="d-flex align-center">
                <v-avatar size="48" color="rgba(255,255,255,0.15)" class="mr-3">
                  <v-icon dark>{{ daycareDialog.new ? "mdi-account-plus" : "mdi-account-edit" }}</v-icon>
                </v-avatar>
                <div>
                  <div class="text-h6 font-weight-bold white--text">{{ daycareDialog.title }}</div>
                  <div class="text-caption white--text opacity-80" v-if="daycareDialog.date">
                    {{ $moment(daycareDialog.date).format("dddd, DD. MMMM YYYY") }}
                  </div>
                </div>
              </div>
              <v-btn icon dark @click="showDaycareDialog = false"><v-icon>mdi-close</v-icon></v-btn>
            </div>
          </div>
        </div>

        <v-card-text class="pt-6 pb-2">
          <div v-if="!daycareDialog.new" class="user-info-block mb-5">
            <div v-if="daycareDialog.user">
              <div class="d-flex align-center mb-2">
                <v-icon left small color="primary">mdi-account-circle</v-icon>
                <span class="font-weight-bold text-subtitle-1"> {{ daycareDialog.user.vorname }} {{ daycareDialog.user.nachname }} </span>
              </div>
              <div v-if="daycareDialog.user.telefon" class="info-line"><v-icon x-small left>mdi-phone</v-icon>{{ daycareDialog.user.telefon }}</div>
              <div v-if="daycareDialog.user.email" class="info-line"><v-icon x-small left>mdi-email</v-icon>{{ daycareDialog.user.email }}</div>
              <div class="info-line grey--text text--lighten-1 mt-1">
                <v-icon x-small left>mdi-clock-outline</v-icon>
                Angemeldet: {{ $moment(daycareDialog.signupDate).format("DD.MM.YY HH:mm") }} Uhr
              </div>
            </div>
            <p v-else class="mb-0">Benutzer wurde nicht gefunden.</p>
          </div>

          <user-search
            v-else
            dark
            outlined
            :key="daycareDialog.user"
            :value="daycareDialog.user"
            label="Kunde suchen"
            rights="g_daycare"
            @update="updateUser"
            class="mb-4"></user-search>

          <div class="section-label">Anzahl</div>
          <v-row no-gutters dense>
            <v-col cols="6">
              <v-text-field
                outlined
                dense
                v-model.number="daycareDialog.childrenAmount"
                label="Kinder"
                prepend-inner-icon="mdi-human-child"
                type="number"
                min="0"></v-text-field>
            </v-col>
            <v-col cols="6">
              <v-text-field
                outlined
                dense
                v-model.number="daycareDialog.amountBabies"
                label="Babies"
                prepend-inner-icon="mdi-baby-carriage"
                type="number"
                min="0"></v-text-field>
            </v-col>
          </v-row>

          <div class="section-label">Zeiten</div>
          <v-row no-gutters dense>
            <v-col cols="6">
              <v-menu
                ref="arrivedAtPicker"
                v-model="showArrivedAtPicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="daycareDialog.arrivedAt"
                    label="Check-in"
                    prepend-inner-icon="mdi-login"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"></v-text-field>
                </template>
                <v-time-picker
                  v-if="showArrivedAtPicker"
                  format="24hr"
                  v-model="daycareDialog.arrivedAt"
                  @click:minute="$refs.arrivedAtPicker.save(daycareDialog.arrivedAt)"></v-time-picker>
              </v-menu>
            </v-col>
            <v-col cols="6">
              <v-menu
                ref="leftAtPicker"
                v-model="showLeftAtPicker"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="daycareDialog.leftAt"
                    label="Check-out"
                    prepend-inner-icon="mdi-logout"
                    readonly
                    outlined
                    dense
                    v-bind="attrs"
                    v-on="on"></v-text-field>
                </template>
                <v-time-picker
                  v-if="showLeftAtPicker"
                  format="24hr"
                  v-model="daycareDialog.leftAt"
                  @click:minute="$refs.leftAtPicker.save(daycareDialog.leftAt)"></v-time-picker>
              </v-menu>
            </v-col>
          </v-row>

          <div class="section-label">
            Kinder
            <span class="section-label-hint">Namen der Kinder, die heute betreut werden</span>
          </div>
          <v-combobox
            v-model="daycareDialog.childNames"
            :items="availableChildNames"
            label="Kinder auswählen oder neue Namen eingeben"
            prepend-inner-icon="mdi-human-child"
            multiple
            chips
            small-chips
            deletable-chips
            outlined
            dense
            hide-selected
            hint="Tippe einen neuen Namen und drücke Enter, um ihn hinzuzufügen"
            persistent-hint
            class="mb-4">
            <template v-slot:no-data>
              <v-list-item>
                <v-list-item-content>
                  <v-list-item-title> Drücke <kbd>Enter</kbd>, um einen neuen Namen hinzuzufügen </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </template>
          </v-combobox>

          <div class="section-label">Trainingsort der Eltern</div>
          <div class="location-selector mb-4">
            <div
              v-for="loc in locationOptions"
              :key="loc.value"
              class="location-option"
              :class="{ active: daycareDialog.location === loc.value }"
              @click="toggleLocation(loc.value)">
              <v-icon small :color="daycareDialog.location === loc.value ? 'primary' : 'grey lighten-1'">
                {{ loc.icon }}
              </v-icon>
              <span class="location-label">{{ loc.label }}</span>
              <v-icon v-if="daycareDialog.location === loc.value" small color="primary" class="check-icon"> mdi-check-circle </v-icon>
            </div>
          </div>

          <div class="section-label">
            Notiz
            <span class="section-label-hint">Besondere Hinweise (pro Anmeldung)</span>
          </div>
          <v-textarea
            outlined
            v-model="daycareDialog.note"
            placeholder="Z.B. Allergien, besondere Hinweise, Abholberechtigte ..."
            prepend-inner-icon="mdi-note-text-outline"
            rows="3"
            auto-grow
            counter="250"
            maxlength="250"></v-textarea>
        </v-card-text>

        <v-card-actions class="pa-4 pt-0">
          <v-btn large text @click="showDaycareDialog = false">Abbrechen</v-btn>
          <v-spacer></v-spacer>
          <v-btn large color="primary" class="white--text rounded-lg px-6" @click="updateEntry()"> <v-icon left>mdi-check</v-icon>Speichern </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
// Der Script-Teil ist unverändert geblieben, um die Funktionalität zu garantieren.
import api from "../../../api"
import moment from "moment"
import userSearch from "../../../components/userSearch.vue"

export default {
  components: { userSearch },
  data() {
    return {
      dateRange: [
        moment().startOf("isoWeek").format("YYYY-MM-DD"), // Aktuelle Woche als Standard
        moment().add(3, "days").endOf("isoWeek").format("YYYY-MM-DD"),
      ],
      locationOptions: [
        { value: "groupFitness", label: "Group Fitness", icon: "mdi-dumbbell" },
        { value: "trainingArea", label: "Trainingsfläche", icon: "mdi-weight-lifter" },
        { value: "wellness", label: "Wellness", icon: "mdi-spa" },
      ],
      dateMenu: false,
      formattedDateRange: "",
      showArrivedAtPicker: false,
      showLeftAtPicker: false,
      showDaycareDialog: false,
      statistic: null,
      totalChildren: null,
      totalBabies: null,
      today: moment().format("YYYY-MM-DD"),
      tomorrow: moment().add(1, "days").format("YYYY-MM-DD"),
      daycareDialog: {
        title: "",
        childrenAmount: 0,
        amountBabies: 0,
        new: false,
        date: null,
        user: null,
        signupId: null,
        arrivedAt: null,
        leftAt: null,
        signupDate: null,
        note: "",
        location: null, // NEU
        childNames: [], // NEU
      },
    }
  },
  created() {
    this.updateFormattedDateRange()
    this.fetchDaycareStatistic()
  },
  computed: {
    availableChildNames() {
      if (!this.daycareDialog.user || !this.daycareDialog.user.daycareChildren) return []
      return this.daycareDialog.user.daycareChildren.map((c) => c.name)
    },
  },
  methods: {
    toggleLocation(value) {
      // Nochmal klicken hebt die Auswahl wieder auf
      this.daycareDialog.location = this.daycareDialog.location === value ? null : value
    },
    locationLabel(location) {
      return { groupFitness: "Group Fitness", trainingArea: "Trainingsfläche", wellness: "Wellness" }[location] || ""
    },
    locationIcon(location) {
      return { groupFitness: "mdi-dumbbell", trainingArea: "mdi-weight-lifter", wellness: "mdi-spa" }[location] || "mdi-map-marker"
    },
    updateFormattedDateRange() {
      if (this.dateRange.length === 2 && this.dateRange[0] && this.dateRange[1]) {
        let start = moment(this.dateRange[0])
        let end = moment(this.dateRange[1])
        // Sicherstellen, dass start vor end ist
        if (start.isAfter(end)) {
          ;[start, end] = [end, start]
          this.dateRange = [start.format("YYYY-MM-DD"), end.format("YYYY-MM-DD")]
        }
        this.formattedDateRange = `${start.format("DD.MM.YYYY")} - ${end.format("DD.MM.YYYY")}`
      }
    },
    applyDateRange() {
      this.dateMenu = false
      this.updateFormattedDateRange()
      this.fetchDaycareStatistic()
    },
    async fetchDaycareStatistic() {
      if (this.dateRange.length < 2 || !this.dateRange[0] || !this.dateRange[1]) return

      const start = this.dateRange[0]
      const end = this.dateRange[1]
      let statisticDB = await api.fetchDaycareStatistic(start, end).catch((err) => console.log(err))

      if (statisticDB) {
        statisticDB.forEach((entry) => {
          entry.signups.sort((a, b) => {
            if (!a.arrivalTime) return 1
            if (!b.arrivalTime) return -1
            return moment(a.arrivalTime, "HH:mm").diff(moment(b.arrivalTime, "HH:mm"))
          })
        })

        this.statistic = statisticDB

        this.totalChildren = statisticDB.reduce((total, entry) => total + entry.childrenAmount, 0)
        this.totalBabies = statisticDB.reduce((total, entry) => total + entry.amountBabies, 0)
      }
    },
    async updateEntry() {
      if (this.daycareDialog.new) {
        if ((this.daycareDialog.childrenAmount > 0 || this.daycareDialog.amountBabies > 0) && this.daycareDialog.user) {
          let status = await api.signupDaycareAdmin(this.daycareDialog).catch((e) => console.log(e))
          if (status) {
            this.showDaycareDialog = false
            this.fetchDaycareStatistic()
          }
        } else {
          alert("Bitte einen Kunden auswählen und mindestens ein Kind oder Baby angeben.")
        }
      } else {
        let status = await api.signupDaycareAdmin(this.daycareDialog).catch((e) => console.log(e))
        if (status) {
          this.showDaycareDialog = false
          this.fetchDaycareStatistic()
        }
      }
    },
    updateUser(user) {
      console.log(user)
      this.daycareDialog.user = user
    },
    addDaycareSignup(date) {
      this.daycareDialog = {
        date: date,
        new: true,
        signupId: null,
        title: "Neue Anmeldung",
        childrenAmount: 0,
        amountBabies: 0,
        arrivedAt: null,
        leftAt: null,
        user: null,
        signupDate: null,
        note: "",
        location: null, // NEU
        childNames: [], // NEU
      }
      this.showDaycareDialog = true
    },
    editDaycareSignup(signupId, entry) {
      const signup = entry.signups.find((s) => s._id === signupId)
      if (signup) {
        this.daycareDialog = {
          date: entry.date,
          signupId: signupId,
          new: false,
          title: "Anmeldung bearbeiten",
          signupDate: signup.date,
          user: signup.user,
          leftAt: signup.leftAt || null,
          arrivedAt: signup.arrivedAt || null,
          childrenAmount: signup.amountChildren || 0,
          amountBabies: signup.amountBabies || 0,
          note: signup.note || "",
          location: signup.location || null, // NEU
          childNames: signup.childNames || [], // NEU
        }
        this.showDaycareDialog = true
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .signup-dialog-wrapper {
  overflow-y: hidden;

  // Card auf volle Dialog-Höhe ziehen
  > .signup-dialog {
    display: flex;
    flex-direction: column;
    max-height: 90vh; // oder was du willst

    .v-card__text {
      overflow-y: auto;
      flex: 1;
    }
  }
}
.location-selector {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.location-option {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background: rgba(255, 255, 255, 0.07);
    border-color: rgba(255, 255, 255, 0.15);
  }

  &.active {
    background: rgba(var(--v-primary-rgb, 33, 150, 243), 0.12);
    border-color: var(--v-primary-base);
  }

  .location-label {
    flex-grow: 1;
    font-size: 0.95rem;
    font-weight: 500;
  }

  .check-icon {
    margin-left: auto;
  }
}

// Auf Desktop nebeneinander anzeigen
@media (min-width: 600px) {
  .location-selector {
    flex-direction: row;

    .location-option {
      flex: 1;
      justify-content: center;
      padding: 14px 12px;

      .check-icon {
        position: absolute;
        top: 6px;
        right: 6px;
      }
    }

    .location-option {
      position: relative;
    }
  }
}
// Animation für das Einblenden der Karten
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Hauptkarte für jeden Tag
.day-card {
  background-color: #212121 !important; // Ein tieferes, satteres Schwarz
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  margin-bottom: 24px;
  // Animation anwenden
  opacity: 0; // Startet unsichtbar
  animation: fadeInUp 0.6s ease-out forwards;
  transition: all 0.3s ease-out;

  &:hover {
    transform: translateY(-5px) scale(1.02);
    border-color: var(--v-primary-base);
    box-shadow: 0 8px 40px -10px rgba(var(--v-primary-rgb, 0, 0, 0), 0.6);
  }
}

.day-card-title {
  font-weight: bold;
  font-size: 1.2rem;
  padding: 16px 24px;
}

.day-card-footer {
  background-color: rgba(0, 0, 0, 0.2);
  font-size: 0.9rem;
  padding: 12px 24px;
}

// Jede einzelne Anmeldung in der Liste
.signup-list-item {
  padding: 8px 24px;
}

// Der Speicher-Button im Dialog
.save-btn {
  background: linear-gradient(45deg, var(--v-primary-base), var(--v-primary-darken1)) !important;
  box-shadow: 0 4px 15px 0 rgba(0, 0, 0, 0.4) !important;
}

.signup-dialog {
  border-radius: 20px !important;
  overflow: hidden;
}

.dialog-header {
  position: relative;
  padding: 20px 24px;
  overflow: hidden;

  .dialog-header-bg {
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, var(--v-primary-base), var(--v-primary-darken1));
    opacity: 0.9;
    z-index: 0;
  }

  .dialog-header-content {
    position: relative;
    z-index: 1;
    display: flex;
    width: 100%;
  }

  .opacity-80 {
    opacity: 0.8;
  }
}

.user-info-block {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 14px 16px;

  .info-line {
    font-size: 0.85rem;
    line-height: 1.6;
  }
}

.section-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.6);
  font-weight: 600;
  margin: 8px 0 8px 4px;

  .section-label-hint {
    text-transform: none;
    letter-spacing: 0;
    font-weight: 400;
    opacity: 0.7;
    margin-left: 6px;
  }
}

.note-preview {
  color: rgba(255, 255, 255, 0.75) !important;
  font-style: italic;
  margin-top: 2px;
}
</style>
